import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import config from '../config/env';

/**
 * Custom hook to initialize and manage the OpenReplay tracker.
 *
 * The tracker is imported dynamically rather than at module scope. It used to
 * be constructed as a side effect of importing this file --
 * `const tracker = new Tracker(...)` at the top level -- so the 83 KB library
 * was pulled into the initial bundle and instantiated in *every* environment,
 * even though only `tracker.start()` was ever gated on production. Local dev
 * and the dev deployment paid for a recorder they never start.
 *
 * Loading it inside the production branch means the chunk is only fetched
 * where it is actually used, and a failure to load it can no longer take the
 * app down with it.
 */
const useOpenReplay = () => {
  useEffect(() => {
    if (!config.isProduction) return undefined;

    let tracker = null;
    // Guards the async gap: if the component unmounts before the dynamic
    // import resolves, there is nothing to start and nothing to stop.
    let cancelled = false;

    import('@openreplay/tracker')
      .then(({ default: Tracker }) => {
        if (cancelled) return;

        tracker = new Tracker({ projectKey: config.openReplay.projectKey });
        tracker.start();

        if (!sessionStorage.getItem('trackingSessionId')) {
          sessionStorage.setItem('trackingSessionId', uuidv4());
        }
      })
      .catch((error) => {
        // Session replay is not worth breaking a page over.
        console.error('OpenReplay failed to load:', error);
      });

    return () => {
      cancelled = true;
      tracker?.stop();
    };
  }, []);
};

export default useOpenReplay;
